import api from '../../services/api';

/*
 * The orders as seen from inside the warehouse.
 *
 * Their warehouse state is not a column of any table: the server works it out
 * from the reservations, from what was prepared on each line and from the
 * closed parcels, every time it is asked. So every answer carries, next to the
 * state, what it was deduced from.
 */

/**
 * The last thirty days by default, paginated on the server.
 *
 * The answer carries `state_counts` for every state over the other filters,
 * `window_from` (the date it looked back to) and `elsewhere` — how many orders
 * there are in other warehouses when one is picked, and which. The two exist
 * for the same reason: an empty table looks exactly like a failure.
 *
 * @param {object} params - `warehouse, company, state, source, search,
 *   date_from, date_to, page, page_size`
 */
export const getWmsOrdersRequest = async (params) => {
  return await api.get('wms/orders/', { params });
};

/** The catalogs to filter by: states, the flow, and the channels. */
export const getWmsOrdersFiltersRequest = async (params) => {
  return await api.get('wms/orders/filters/', { params });
};

/** One order with its `lines`, its `packages` and its `history`. */
export const getWmsOrderRequest = async (orderId) => {
  return await api.get(`wms/orders/${orderId}/`);
};

/**
 * Cancels before it leaves the building. The server releases the reservations
 * and leaves what was picked on the packing table, and answers
 * `{ order, released_units, staged_units, courier }`.
 */
export const cancelWmsOrderRequest = async (orderId, reason = '') => {
  return await api.post(`wms/orders/${orderId}/cancel/`, { reason });
};

/**
 * Reserves what the lines still lack, with what there is. The same endpoint
 * the preparing screen calls; here it is the one thing a seller may do in its
 * operator's centre when the contract opens it. Answers the row plus
 * `{ reserved_now, lines_complete, lines_total }`.
 */
export const reserveWmsOrderRequest = async (orderId) => {
  return await api.post(`wms/orders/${orderId}/reserve/`);
};

/**
 * Reserves several orders at once. Each one goes through the same rules as a
 * single reserve; the ones that cannot come back in `problems` with why, and
 * the rest are reserved anyway. Answers `{ reserved, problems, units, message }`.
 */
export const reserveWmsOrdersBulkRequest = async (orderIds) => {
  return await api.post('wms/orders/bulk/reserve/', { order_ids: orderIds });
};

/**
 * Cancels several orders at once, with the rules of a single cancel, order by
 * order. Answers `{ cancelled, problems, message }`.
 */
export const cancelWmsOrdersBulkRequest = async (orderIds, reason = '') => {
  return await api.post('wms/orders/bulk/cancel/', { order_ids: orderIds, reason });
};
